import { type FC, memo } from "react";
import { token } from "@stubhub/broadway-foundation/tokens";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { MobileCredibilityBadge } from "src/website-apps/responsive/stubhub-performer/components/mobile-credibility-badge/MobileCredibilityBadge";
import { MobileCredibilityBadgePlacement } from "src/website-apps/responsive/stubhub-performer/components/mobile-credibility-badge/MobileCredibilityBadgePlacement";
import { PerformerNativeHeader } from "src/website-apps/responsive/stubhub-performer/components/page-header/native-header/PerformerNativeHeader";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { MarketplaceDisclosure } from "src/website-apps/shared/components/disclosures";
import { ErrorMessage } from "src/website-apps/shared/components/ErrorMessage";
import { EnsembleAppNavHeader } from "src/website-apps/shared/components/header/ensemble-app-nav/EnsembleAppNavHeader";
import { EventsGridContextAwareHeader } from "src/website-apps/shared/components/header/EventsGridContextAwareHeader";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { MegaSearchContext, useMegaSearchContext } from "src/website-apps/shared/components/mega-search";
import { canShowWorldCupFanProtectDisplay } from "src/website-apps/shared/components/world-cup-fan-protect/eligibility";
import { useIsCalendarView } from "src/website-apps/shared/hooks/useIsCalendarView";
import {
  PerformerLabelType,
  useWillShowPerformerCredibilityLabel,
} from "src/website-apps/shared/hooks/useWillShowPerformerCredibilityLabel";
import { headerContainer, officialMarketplaceWrapper } from "./styles";

interface Props {
  isNativeApp: boolean;
  isUserLoggedIn: boolean;
  setMobileMenuOpen?: (isOpen: boolean) => void;
}

/** Header for performer page, includes native header, mega search, and navigation links. */
export const Header: FC<Props> = memo(({ isNativeApp, isUserLoggedIn, setMobileMenuOpen }) => {
  const appContext = useAppContext();
  const model = usePerformerPageModelData();
  const megaSearchContext = useMegaSearchContext();
  const { isOnCalendarView } = useIsCalendarView();

  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const useEnsembleHeader = model.useEnsembleHeader ?? false;

  const labelType = useWillShowPerformerCredibilityLabel();
  const canShowWorldCupFanProtect = canShowWorldCupFanProtectDisplay(
    model.categorySummary.categoryId,
    appContext.cobrandId,
  );
  const hideFanProtectMobileLabel =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMWorldCupFanProtectDisplay,
      false,
      true,
      () => labelType === PerformerLabelType.FAN_PROTECT_MOBILE && canShowWorldCupFanProtect,
      false,
    ) ?? false;
  const showCredibilityLabel =
    labelType !== PerformerLabelType.NONE &&
    !(labelType === PerformerLabelType.FAN_PROTECT_MOBILE && hideFanProtectMobileLabel);

  return (
    <MegaSearchProvider>
      <PerformerNativeHeader performerName={model.categorySummary.categoryTitle ?? ""} />
      <div className={headerContainer({ view: isOnCalendarView ? "calendar" : "default" })}>
        <ErrorMessage />
        {!isNativeApp && !megaSearchContext.active && (
          <MarketplaceDisclosure marketplaceDisclosureSiteMessage={model.header.marketplaceDisclosure} />
        )}
        <MegaSearchContext.Provider value={megaSearchContext}>
          {useEnsembleHeader ? (
            <EnsembleAppNavHeader isUserLoggedIn={isUserLoggedIn} />
          ) : (
            <EventsGridContextAwareHeader
              browseHeader
              discoveryLinks={model.header.navigationBarLinks}
              isUserLoggedIn={isUserLoggedIn}
              onMobileMenuToggle={setMobileMenuOpen}
              bigPerformerBanner
              dynamicTextColor={token(isDesktop ? "colors.text" : "colors.text.subtle")}
            />
          )}
        </MegaSearchContext.Provider>
      </div>
      {!isDesktop && (
        <MobileCredibilityBadge
          placement={MobileCredibilityBadgePlacement.CONTROL}
          visible={showCredibilityLabel}
          wrapperClassName={officialMarketplaceWrapper}
        />
      )}
    </MegaSearchProvider>
  );
});
Header.displayName = "Header";
